<template>
  <v-container fluid class="pa-0 landing">

    <!-- HERO -->
    <section class="hero d-flex align-center justify-center">
      <div class="landing-language reveal">
        <LanguageSelector variant="landing" />
      </div>

      <v-container class="text-center white--text">
        <v-img
          :src="require('@/assets/logo.png')"
          contain
          height="200"
          class="mb-6 floaty"
        />

        <h1 class="display-2 font-weight-bold mb-4 reveal">
          {{ $t('landing.heroTitle') }}
        </h1>

        <p class="subtitle-1 mb-3 reveal reveal-delay-1">
          {{ $t('landing.heroText') }}
        </p>

        <!-- CTA BUTTONS -->
        <div class="hero-actions reveal reveal-delay-3">
          <v-row justify="center" dense>
            <v-col cols="6" sm="auto">
              <v-btn
                color="white"
                class="login-cta purple--text text--darken-3 px-8 cta-pop"
                rounded
                large
                to="/login"
              >
                <v-icon left>mdi-login</v-icon>
                {{ $t('common.managerAccess') }}
              </v-btn>
            </v-col>

            <v-col cols="6" sm="auto">
              <v-btn
                outlined
                color="white"
                class="px-8 cta-pop"
                rounded
                large
                to="/request-access"
              >
                {{ $t('common.startFree') }}
              </v-btn>
            </v-col>
          </v-row>
        </div>

      </v-container>
    </section>

    <!-- APP PROMO -->
    <section class="app-section py-12">
      <v-container>
        <v-layout wrap align-center justify-space-between>

          <!-- TEXT -->
          <v-flex xs12 md6 class="reveal">
            <h2 class="font-weight-bold mb-4">
              {{ $t('landing.appTitle') }}
            </h2>

            <p class="subtitle-1 mb-6">
              {{ $t('landing.appText') }}
            </p>

            <div class="bullets">
              <div v-for="bullet in bullets" :key="bullet" class="bullet">
                <v-icon color="purple darken-2" class="mr-2">mdi-check-circle</v-icon>
                {{ $t(bullet) }}
              </div>
            </div>

            <div class="app-download-actions mt-6">
              <a
                class="google-play-link"
                href="https://play.google.com/store/apps/details?id=com.thevalkiria.konvenio"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="$t('landing.googlePlayAlt')"
              >
                <img
                  :src="require('@/assets/google-play-badge.png')"
                  :alt="$t('landing.googlePlayAlt')"
                  class="google-play-badge"
                >
              </a>

              <a
                class="ios-web-link"
                href="/ios/"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="$t('landing.iosWebAlt')"
              >
                <v-icon class="ios-web-icon">mdi-apple</v-icon>
                {{ $t('landing.iosWebButton') }}
              </a>
            </div>
          </v-flex>

          <!-- MOCKUP -->
          <v-flex xs12 md5 class="text-center mt-10 mt-md-0 reveal reveal-delay-1">
            <v-img
              :src="require('@/assets/app-mockup.jpg')"
              max-width="260"
              contain
              class="mock-rounded floaty-slow"
            />
          </v-flex>

        </v-layout>
      </v-container>
    </section>

    <!-- PRICING / PILOT BLOCK COMMENTED OUT
    <section ref="pricing" class="pricing-section py-14">
      <v-container>

        <div class="text-center mb-10 reveal">
          <h2 class="font-weight-bold mb-3">{{ $t('landing.pricingTitle') }}</h2>
          <p class="subtitle-1 mb-2">
            {{ $t('landing.pilotIntro') }}
          </p>
          <p class="caption grey--text text--darken-1">
            {{ $t('landing.pricingCaption') }}
          </p>
        </div>

        <v-row justify="center" class="reveal reveal-delay-1">
          <v-col cols="12" md="10" lg="9">
            <v-card class="pricing-card" elevation="0">

              <div class="free-banner">
                <v-icon left color="purple darken-2">mdi-gift</v-icon>
                <strong>{{ $t('landing.freePeriodLabel') }}</strong> {{ $t('landing.freePeriodText') }}
              </div>

              <div class="pilot-message pa-6 pa-md-8">
                <v-icon size="42" color="purple darken-2" class="mb-4">mdi-rocket-launch</v-icon>
                <h3 class="font-weight-bold mb-3">{{ $t('landing.pilotMessageTitle') }}</h3>
                <p class="subtitle-1 mb-0">{{ $t('landing.pilotMessageText') }}</p>
              </div>

              <v-divider class="my-6"></v-divider>

              <v-row align="center" class="px-4 pb-4">
                <v-col cols="12" md="8">
                  <div class="pricing-notes">
                    <div v-for="note in pricingNotes" :key="note" class="note">
                      <v-icon small color="purple darken-2" class="mr-2">mdi-check</v-icon>
                      {{ $t(note) }}
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="text-md-right">
                  <v-btn
                    color="purple darken-2"
                    class="white--text px-8 cta-pop"
                    rounded
                    large
                    to="/request-access"
                  >
                    {{ $t('common.startFree') }}
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

      </v-container>
    </section>
    -->

    <!-- FEATURES -->
    <section class="features py-12">
      <v-container>
        <v-layout wrap>

          <v-flex
            v-for="feature in features"
            :key="feature.title"
            xs12
            sm6
            md3
            class="pa-4 reveal"
            :class="feature.delay"
          >
            <div class="feature-card hover-lift">
              <v-icon size="40" color="purple darken-2">
                {{ feature.icon }}
              </v-icon>
              <h3 class="mt-3 font-weight-bold">{{ $t(feature.title) }}</h3>
              <p>{{ $t(feature.text) }}</p>
            </div>
          </v-flex>

        </v-layout>
      </v-container>
    </section>

    <!-- FAQ BLOCK COMMENTED OUT
    <section class="faq py-14">
      <v-container>
        <div class="text-center mb-10 reveal">
          <h2 class="font-weight-bold mb-3">{{ $t('landing.faqTitle') }}</h2>
          <p class="subtitle-1">{{ $t('landing.faqSubtitle') }}</p>
        </div>

        <v-row justify="center">
          <v-col cols="12" md="10" lg="9">
            <v-expansion-panels accordion class="reveal reveal-delay-1">

              <v-expansion-panel v-for="faq in faqs" :key="faq.question">
                <v-expansion-panel-header>
                  {{ faq.question }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  {{ faq.answer }}
                </v-expansion-panel-content>
              </v-expansion-panel>

            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-container>
    </section>
    -->

    <!-- FOOTER CTA -->
    <section class="footer-cta py-14">
      <v-container class="text-center reveal">
        <h2 class="font-weight-bold mb-3">{{ $t('landing.footerTitle') }}</h2>
        <p class="subtitle-1 mb-6">
          {{ $t('landing.footerTextBefore') }}{{ $t('landing.footerTextAfter') }}
        </p>
        <v-btn
          color="purple darken-2"
          class="white--text px-10 cta-pop"
          rounded
          x-large
          to="/request-access"
        >
          {{ $t('common.startFree') }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
        <div class="landing-version mt-6">
          v{{ appVersion }}
        </div>
        <div class="legal-links mt-3">
          <router-link to="/aviso-legal" target="_blank" rel="noopener">{{ $t('info.legalTitle') }}</router-link>
          <span aria-hidden="true">|</span>
          <router-link to="/politica-de-privacidad" target="_blank" rel="noopener">{{ $t('info.privacyTitle') }}</router-link>
          <span aria-hidden="true">|</span>
          <router-link to="/politica-de-cookies" target="_blank" rel="noopener">{{ $t('info.cookiesTitle') }}</router-link>
          <span class="legal-version">v{{ legalTermsVersion }}</span>
        </div>
      </v-container>
    </section>

  </v-container>
</template>

<script>
import LanguageSelector from '@/components/LanguageSelector.vue'
import packageInfo from '../../package.json'
import { LEGAL_TERMS_VERSION } from '@/config/legal'

export default {
  name: 'Landing',
  components: {
    LanguageSelector
  },
  computed: {
    appVersion() {
      return packageInfo.version
    },
    legalTermsVersion() {
      return LEGAL_TERMS_VERSION
    },
    bullets() {
      return [
        'landing.bulletInvite',
        'landing.bulletRoles',
        'landing.bulletDocs'
      ]
    },
    // pricingNotes() {
    //   return [
    //     'landing.notePanel',
    //     'landing.noteTools',
    //     'landing.noteRoles'
    //   ]
    // },
    features() {
      return [
        { icon: 'mdi-bulletin-board', title: 'landing.features.noticesTitle', text: 'landing.features.noticesText' },
        { icon: 'mdi-book-open-page-variant', title: 'landing.features.agreementsTitle', text: 'landing.features.agreementsText', delay: 'reveal-delay-1' },
        { icon: 'mdi-calendar', title: 'landing.features.calendarTitle', text: 'landing.features.calendarText', delay: 'reveal-delay-2' },
        { icon: 'mdi-chat-question', title: 'landing.features.suggestionsTitle', text: 'landing.features.suggestionsText', delay: 'reveal-delay-3' }
      ]
    },
    // faqs() {
    //   const messages = this.$i18n.messages[this.$i18n.locale] || this.$i18n.messages.en
    //   return messages.landing.faqs
    // }
  },
  mounted() {
    // Animacion reveal on scroll
    const els = Array.from(this.$el.querySelectorAll('.reveal'))
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible')
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12 }
    )
    els.forEach((el) => io.observe(el))
  },
  // methods: {
  //   scrollToPricing() {
  //     const el = this.$refs.pricing
  //     if (!el) return
  //     el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  //   }
  // }
}
</script>

<style scoped>
/* HERO */
.hero {
  min-height: 60vh;
  background: linear-gradient(135deg, #7b2ff7, #ffffff);
  position: relative;
  overflow: hidden;
}

/* subtle animated shapes */
.hero::before,
.hero::after {
  content: "";
  position: absolute;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background: rgba(123, 47, 247, 0.18);
  filter: blur(2px);
  animation: blob 10s ease-in-out infinite alternate;
  z-index: 0;
}
.hero::before {
  top: -140px;
  left: -140px;
}
.hero::after {
  bottom: -160px;
  right: -160px;
  animation-delay: 0.6s;
}
@keyframes blob {
  0% { transform: translate(0,0) scale(1); }
  100% { transform: translate(30px, -20px) scale(1.08); }
}

/* Ensure content above shapes */
.hero > .container {
  position: relative;
  z-index: 1;
}

.landing-language {
  position: absolute;
  top: 24px;
  right: 32px;
  z-index: 2;
}

@media (max-width: 600px) {
  .hero {
    min-height: 72vh;
    padding: 68px 0 34px;
  }

  .hero::before,
  .hero::after {
    height: 260px;
    width: 260px;
  }

  .hero .v-image {
    max-height: 132px;
  }

  .hero h1 {
    font-size: 2rem !important;
    line-height: 1.14;
  }

  .hero-actions .v-btn {
    min-width: 0 !important;
    padding-left: 10px !important;
    padding-right: 10px !important;
    width: 100%;
  }

  .landing-language {
    top: 14px;
    right: 14px;
  }

  .free-banner {
    align-items: flex-start;
    font-size: 14px;
  }

  .feature-card {
    padding: 22px;
  }
}

/* APP SECTION */
.app-section {
  background: #faf7ff;
  --store-badge-height: 54px;
  --store-badge-width: 182px;
}

/* Bullets */
.bullets .bullet {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.app-download-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.google-play-link,
.ios-web-link {
  border-radius: 8px;
  display: inline-flex;
  height: var(--store-badge-height);
  overflow: hidden;
  transition: transform 0.15s ease, filter 0.15s ease;
  width: var(--store-badge-width);
}

.google-play-badge {
  display: block;
  height: 100%;
  width: 100%;
}

.ios-web-link {
  align-items: center;
  background: #000 !important;
  border: 1px solid rgba(74, 20, 140, 0.18);
  box-sizing: border-box;
  color: #fff !important;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  justify-content: center;
  text-decoration: none;
}

.ios-web-icon {
  color: #fff !important;
  font-size: 20px;
  margin: 0;
}

.google-play-link:hover,
.ios-web-link:hover {
  filter: brightness(1.08);
  transform: translateY(-2px);
}

/* Mockup */
.mock-rounded {
  display: inline-block;
  border: 5px solid #000;
  border-radius: 22px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 25px rgba(0,0,0,0.18);
}

@media (max-width: 600px) {
  .app-section {
    --store-badge-height: 40px;
    --store-badge-width: 136px;
    padding-bottom: 28px !important;
    padding-top: 36px !important;
    text-align: left;
  }

  .bullets .bullet {
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.35;
  }

  .bullets .v-icon {
    margin-top: 1px;
  }

  .app-download-actions {
    align-items: center;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 8px;
    justify-content: center;
    max-width: 100%;
  }

  .ios-web-link {
    font-size: 12px;
    gap: 6px;
  }

  .ios-web-icon {
    font-size: 18px;
  }

  .mock-rounded {
    max-width: calc(100vw - 96px) !important;
  }
}

/* FEATURES */
.feature-card {
  background: white;
  padding: 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08);
}

/* Hover lift for cards */
.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-lift:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 28px rgba(0,0,0,0.12);
}

/*
PRICING / PILOT STYLES COMMENTED OUT
.pricing-section {
  background: #ffffff;
}
.pricing-card {
  border-radius: 20px;
  border: 1px solid rgba(123,47,247,0.12);
  box-shadow: 0 10px 30px rgba(0,0,0,0.06);
  overflow: hidden;
}
.pricing-notes .note {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.pilot-message {
  text-align: center;
  background: linear-gradient(180deg, rgba(123,47,247,0.06), rgba(255,255,255,0));
}
.pilot-message h3 {
  color: #4a148c;
}
.pilot-message p {
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
}
.free-banner {
  background: rgba(123, 47, 247, 0.08);
  color: #4a148c;
  padding: 14px 18px;
  font-size: 15px;
  border-bottom: 1px solid rgba(123,47,247,0.15);
  display: flex;
  align-items: center;
  gap: 8px;
}
*/

/* FAQ styles commented out
.faq {
  background: #faf7ff;
}
*/

/* Footer */
.footer-cta {
  background: linear-gradient(135deg, rgba(123,47,247,0.10), rgba(255,255,255,1));
}

/* Simple float animations */
.floaty { animation: float 3.5s ease-in-out infinite; }
.floaty-slow { animation: float 5s ease-in-out infinite; }
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

/* CTA micro-pop */
.cta-pop {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.cta-pop:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0,0,0,0.14);
}

.login-cta {
  border: 2px solid rgba(74, 20, 140, 0.18);
  font-weight: 800;
  letter-spacing: 0;
  box-shadow: 0 8px 18px rgba(74, 20, 140, 0.18);
}

/* Reveal on scroll */
.reveal {
  opacity: 0;
  transform: translateY(14px);
  transition: opacity 520ms ease, transform 520ms ease;
  will-change: opacity, transform;
}
.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}
.reveal-delay-1 { transition-delay: 90ms; }
.reveal-delay-2 { transition-delay: 160ms; }
.reveal-delay-3 { transition-delay: 230ms; }

/* Trust line */
.trust-line {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.landing-version {
  color: rgba(63, 61, 86, 0.46);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
}

.legal-links {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 8px;
  justify-content: center;
}

.legal-links a {
  color: #4a148c;
  font-weight: 700;
  text-decoration: none;
}

.legal-links a:hover {
  text-decoration: underline;
}

.legal-version {
  color: rgba(63, 61, 86, 0.48);
  font-weight: 700;
}
</style>
